export interface TemplateFolderRecord {
  id: string;
  owner_id: string | null;
  name: string;
  sort_order: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface TemplateFolderFields {
  folder_id: string | null;
  folder_name: string | null;
  folder_sort_order: number | null;
}

export interface TemplateFolderGroup<T extends TemplateFolderFields> {
  key: string;
  label: string;
  folderId: string | null;
  sortOrder: number;
  isUngrouped: boolean;
  templates: T[];
}

export const TEMPLATE_FOLDER_UNGROUPED_KEY = '__ungrouped__';
export const TEMPLATE_FOLDER_UNGROUPED_LABEL = 'Unsorted';
const TEMPLATE_FOLDER_NAME_LIMIT = 80;

const hasOwn = (value: unknown, key: string): boolean =>
  !!value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, key);

const normalizeNullableString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
};

const normalizeNullableNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim().length) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

export const normalizeTemplateFolderName = (value: string): string =>
  value
    .trim()
    .replace(/\s+/g, ' ')
    .slice(0, TEMPLATE_FOLDER_NAME_LIMIT);

export const compareTemplateFolderRecords = (
  left: TemplateFolderRecord,
  right: TemplateFolderRecord,
): number => {
  const sortDiff = left.sort_order - right.sort_order;
  if (sortDiff !== 0) {
    return sortDiff;
  }
  return left.name.localeCompare(right.name, undefined, { sensitivity: 'base' });
};

export const normalizeTemplateFolderRow = (raw: unknown): TemplateFolderRecord | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const id = normalizeNullableString((raw as Record<string, unknown>).id);
  const name = normalizeNullableString((raw as Record<string, unknown>).name);
  if (!id || !name) {
    return null;
  }

  return {
    id,
    owner_id: normalizeNullableString((raw as Record<string, unknown>).owner_id),
    name,
    sort_order: normalizeNullableNumber((raw as Record<string, unknown>).sort_order) ?? 0,
    created_at: normalizeNullableString((raw as Record<string, unknown>).created_at),
    updated_at: normalizeNullableString((raw as Record<string, unknown>).updated_at),
  };
};

export const resolveTemplateFolderFields = (
  raw: unknown,
  previous?: Partial<TemplateFolderFields> | null,
): TemplateFolderFields => {
  const record = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {};
  const previousFields = previous ?? null;

  const hasFolderId = hasOwn(record, 'folder_id');
  const rawFolderId = hasFolderId ? normalizeNullableString(record.folder_id) : undefined;

  const hasFolderObject = hasOwn(record, 'folder');
  const rawFolderObject = hasFolderObject ? normalizeTemplateFolderRow(record.folder) : undefined;

  if (rawFolderId === null || (hasFolderObject && rawFolderObject === null && record.folder === null)) {
    return {
      folder_id: null,
      folder_name: null,
      folder_sort_order: null,
    };
  }

  const folderId = rawFolderObject?.id ?? rawFolderId ?? previousFields?.folder_id ?? null;
  if (!folderId) {
    return {
      folder_id: null,
      folder_name: null,
      folder_sort_order: null,
    };
  }

  return {
    folder_id: folderId,
    folder_name: rawFolderObject?.name ?? previousFields?.folder_name ?? null,
    folder_sort_order: rawFolderObject?.sort_order ?? previousFields?.folder_sort_order ?? null,
  };
};

export const assignTemplateFolderFields = <T extends object>(
  template: T,
  folder: TemplateFolderRecord | null,
): T & TemplateFolderFields => ({
  ...template,
  folder_id: folder?.id ?? null,
  folder_name: folder?.name ?? null,
  folder_sort_order: folder?.sort_order ?? null,
});

export const applyTemplateFolderAssignmentToTemplates = <
  T extends TemplateFolderFields & { id: string }
>(
  templates: T[],
  templateIds: Iterable<string>,
  folder: TemplateFolderRecord | null,
): T[] => {
  const templateIdSet = templateIds instanceof Set ? templateIds : new Set(templateIds);
  if (templateIdSet.size === 0) {
    return templates;
  }

  return templates.map((template) => (
    templateIdSet.has(template.id)
      ? assignTemplateFolderFields(template, folder)
      : template
  ));
};

export const applyTemplateFolderRecordToTemplates = <T extends TemplateFolderFields>(
  templates: T[],
  folder: TemplateFolderRecord,
): T[] => templates.map((template) => (
  template.folder_id === folder.id
    ? assignTemplateFolderFields(template, folder)
    : template
));

export const groupTemplatesByFolder = <T extends TemplateFolderFields>(
  templates: T[],
): Array<TemplateFolderGroup<T>> => {
  const grouped = new Map<string, TemplateFolderGroup<T>>();

  templates.forEach((template) => {
    const folderId = normalizeNullableString(template.folder_id);
    const folderName = normalizeNullableString(template.folder_name);
    const key = folderId ?? TEMPLATE_FOLDER_UNGROUPED_KEY;
    const existing = grouped.get(key);
    if (existing) {
      existing.templates.push(template);
      return;
    }

    grouped.set(key, {
      key,
      label: folderName ?? TEMPLATE_FOLDER_UNGROUPED_LABEL,
      folderId,
      sortOrder: template.folder_sort_order ?? Number.MAX_SAFE_INTEGER,
      isUngrouped: !folderId,
      templates: [template],
    });
  });

  const groups = Array.from(grouped.values());
  groups.sort((left, right) => {
    if (left.isUngrouped !== right.isUngrouped) {
      return left.isUngrouped ? -1 : 1;
    }
    const sortDiff = left.sortOrder - right.sortOrder;
    if (sortDiff !== 0) {
      return sortDiff;
    }
    return left.label.localeCompare(right.label, undefined, { sensitivity: 'base' });
  });

  return groups;
};